import type { TemplateMeasure } from '@/services/api/queryTemplateApi';

export interface TemplatePreviewCell {
  key: string;
  label: string;
  value: string;
}

interface PreviewColumn {
  key: string;
  label: string;
  getValue: (row: Record<string, unknown>) => unknown;
}

type ReadablePrimitive = string | number | boolean;
type ReadableEntry = [key: string, value: ReadablePrimitive];

const PREFERRED_OBJECT_VALUE_KEYS = ['state', 'status', 'value', 'label', 'name'];
const TIME_BUCKET_KEYS = new Set([
  'data_datetime',
  'datetime',
  'timestamp',
  'time',
  'date',
  'created_at',
  'updated_at',
]);
const DATE_TIME_VALUE_PATTERN = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}/;

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isReadablePrimitive(value: unknown): value is ReadablePrimitive {
  if (typeof value === 'string') return value.trim().length > 0;
  return typeof value === 'number' || typeof value === 'boolean';
}

function isReadableEntry(entry: [string, unknown]): entry is ReadableEntry {
  return isReadablePrimitive(entry[1]);
}

function humanizeIdentifier(value: string): string {
  const withoutImplementationPrefix = value
    .replace(/^data[._]/i, '')
    .replace(/^_id[._]/i, '');
  const spaced = withoutImplementationPrefix
    .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!spaced) return value;

  return spaced
    .split(' ')
    .map((part) => {
      const lower = part.toLowerCase();
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join(' ');
}

function normalizeComparableLabel(value: string): string {
  return humanizeIdentifier(value).toLowerCase();
}

function formatTimestampString(value: string): string {
  if (!DATE_TIME_VALUE_PATTERN.test(value)) return value;

  return value
    .replace('T', ' ')
    .replace(/\.\d{3}Z$/, '')
    .replace(/Z$/, '')
    .slice(0, 16);
}

function formatPrimitiveValue(value: ReadablePrimitive, key?: string): string {
  if (typeof value === 'boolean') {
    if (key === 'enabled') return value ? 'enabled' : 'disabled';
    return String(value);
  }

  if (typeof value === 'string') {
    return formatTimestampString(value);
  }

  return String(value);
}

function formatCompactFallback(value: unknown, depth = 0): string {
  if (value == null) return '—';

  if (isReadablePrimitive(value)) {
    return formatPrimitiveValue(value);
  }

  if (Array.isArray(value)) {
    const parts = value.slice(0, 3).map((item) => formatCompactFallback(item, depth + 1));
    return parts.join(', ');
  }

  if (!isRecord(value)) {
    return String(value);
  }

  if (depth > 1) {
    const labels = Object.keys(value).slice(0, 4).map(humanizeIdentifier).join(', ');
    return labels ? `{ ${labels} }` : '—';
  }

  const meaningfulEntries = Object.entries(value).filter(isReadableEntry);
  if (meaningfulEntries.length === 1) {
    const [key, entryValue] = meaningfulEntries[0];
    return formatPrimitiveValue(entryValue, key);
  }

  const compactValues = meaningfulEntries
    .slice(0, 4)
    .map(([key, entryValue]) => `${humanizeIdentifier(key)}: ${formatPrimitiveValue(entryValue, key)}`)
    .join(', ');

  if (compactValues) return compactValues;

  const fallbackLabels = Object.keys(value).slice(0, 4).map(humanizeIdentifier).join(', ');
  return fallbackLabels || '—';
}

export function formatTemplatePreviewValue(value: unknown): string {
  if (value == null) return '—';

  if (isReadablePrimitive(value)) {
    return formatPrimitiveValue(value);
  }

  if (!isRecord(value)) {
    return formatCompactFallback(value);
  }

  for (const key of PREFERRED_OBJECT_VALUE_KEYS) {
    const preferredValue = value[key];
    if (isReadablePrimitive(preferredValue)) {
      return formatPrimitiveValue(preferredValue, key);
    }
  }

  const meaningfulEntries = Object.entries(value).filter(isReadableEntry);
  if (meaningfulEntries.length === 1) {
    const [key, entryValue] = meaningfulEntries[0];
    return formatPrimitiveValue(entryValue, key);
  }

  return formatCompactFallback(value);
}

function hasExistingReadableColumn(existingKeys: string[], candidateLabel: string): boolean {
  const normalizedCandidate = normalizeComparableLabel(candidateLabel);
  return existingKeys.some((key) => key !== '_id' && normalizeComparableLabel(key) === normalizedCandidate);
}

function getIdObjectColumns(idValue: Record<string, unknown>, existingKeys: string[]): PreviewColumn[] {
  const columns: PreviewColumn[] = [];

  for (const [key] of Object.entries(idValue)) {
    const label = TIME_BUCKET_KEYS.has(key.toLowerCase()) ? 'Hour' : humanizeIdentifier(key);
    if (hasExistingReadableColumn(existingKeys, label)) {
      continue;
    }

    columns.push({
      key: `_id.${key}`,
      label,
      getValue: (row) => {
        const id = row._id;
        return isRecord(id) ? id[key] : undefined;
      },
    });
  }

  return columns;
}

function getPreviewColumns(firstRow: Record<string, unknown>, maxColumns: number): PreviewColumn[] {
  const firstRowEntries = Object.entries(firstRow);
  const firstRowKeys = firstRowEntries.map(([key]) => key);
  const columns: PreviewColumn[] = [];

  for (const [key, value] of firstRowEntries) {
    if (key === '_id' && isRecord(value)) {
      columns.push(...getIdObjectColumns(value, firstRowKeys));
      continue;
    }

    columns.push({
      key,
      label: humanizeIdentifier(key),
      getValue: (row) => row[key],
    });
  }

  return columns.slice(0, maxColumns);
}

export function buildTemplatePreviewRows(
  previewData: Record<string, unknown>[],
  maxColumns = 6,
): TemplatePreviewCell[][] {
  const firstRow = previewData[0];
  if (!firstRow) return [];

  const columns = getPreviewColumns(firstRow, maxColumns);
  return previewData.map((row) =>
    columns.map((column) => ({
      key: column.key,
      label: column.label,
      value: formatTemplatePreviewValue(column.getValue(row)).substring(0, 30),
    })),
  );
}

export function formatTemplateMeasureDisplayText(
  measure: Pick<TemplateMeasure, 'field' | 'aggregation' | 'alias'>,
): string {
  if (measure.alias?.trim()) {
    return humanizeIdentifier(measure.alias);
  }

  return `${measure.aggregation}(${humanizeIdentifier(measure.field)})`;
}
